import { useEffect, useRef } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, Button as VButton } from 'vnetwork-ui'
import { Plus, X } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useModal } from '@/contexts/modal'
import { ModalProps } from '@/types/shared'
import { ContractServiceSLA } from '@/modules/Contract/types'
import {
    TIMEZONE_OPTIONS,
    WORK_DAYS_OPTIONS,
} from '@/modules/Service/constants'

export interface SLAConfigModalProps extends ModalProps {
    defaultValues?: Partial<ContractServiceSLA>
    onSave: (data: Partial<ContractServiceSLA>) => void
}

const SLAConfigModal: React.FC<SLAConfigModalProps> = ({
    open,
    onClose,
    onSave,
    defaultValues,
}) => {
    const { t } = useTranslation('contract')
    const { closeModal } = useModal()

    const { control, handleSubmit, reset, watch, setValue } = useForm<
        Partial<ContractServiceSLA>
    >({
        defaultValues: {
            uptime_guarantee: 99.9,
            downtime_allowance_minutes: 43,
            business_hours_only: true,
            business_hours_config: {
                timezone: 'Asia/Ho_Chi_Minh',
                work_days: [1, 2, 3, 4, 5],
                work_hours: { start: '08:00', end: '17:30' },
            },
            sla_rules: [
                {
                    ticket_type_group: 'default',
                    response_time_minutes: 30,
                    resolution_time_minutes: 240,
                },
            ],
            custom_terms: [],
            ...defaultValues,
        },
    })

    const {
        fields: customTermFields,
        append: appendTerm,
        remove: removeTerm,
    } = useFieldArray({
        control,
        name: 'custom_terms',
    })

    const uptime = watch('uptime_guarantee')
    const downtime = watch('downtime_allowance_minutes')
    const responseTime = watch('sla_rules.0.response_time_minutes')
    const resolutionTime = watch('sla_rules.0.resolution_time_minutes')
    const businessHoursOnly = watch('business_hours_only')

    // Track which field is being edited to avoid infinite loop
    const editingField = useRef<'uptime' | 'downtime' | null>(null)

    // Minutes per month (30 days * 24 hours * 60 min = 43200)
    const MINUTES_PER_MONTH = 43200

    // Calculate downtime from uptime
    const calculateDowntime = (uptimePercent: number) => {
        return Math.round(((100 - uptimePercent) * MINUTES_PER_MONTH) / 100)
    }

    // Calculate uptime from downtime
    const calculateUptime = (downtimeMinutes: number) => {
        return (
            Math.round(
                (100 - (downtimeMinutes / MINUTES_PER_MONTH) * 100) * 100
            ) / 100
        )
    }

    useEffect(() => {
        if (open && defaultValues) {
            reset({
                uptime_guarantee: 99.9,
                downtime_allowance_minutes: 43,
                business_hours_only: true,
                business_hours_config: {
                    timezone: 'Asia/Ho_Chi_Minh',
                    work_days: [1, 2, 3, 4, 5],
                    work_hours: { start: '08:00', end: '17:30' },
                },
                sla_rules: [
                    {
                        ticket_type_group: 'default',
                        response_time_minutes: 30,
                        resolution_time_minutes: 240,
                    },
                ],
                custom_terms: [],
                ...defaultValues,
            })
        }
    }, [open, defaultValues, reset])

    const handleClose = () => {
        closeModal()
        onClose?.()
    }

    const onSubmit = (data: Partial<ContractServiceSLA>) => {
        onSave({ ...data, sla_source: 'custom' })
        handleClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={t('contract.sla.custom.title')}
            actions={
                <div className="flex gap-2">
                    <VButton
                        type="button"
                        variant="outlined"
                        onClick={handleClose}
                    >
                        {t('cancel')}
                    </VButton>
                    <VButton type="button" onClick={handleSubmit(onSubmit)}>
                        {t('contract.sla.custom.save')}
                    </VButton>
                </div>
            }
            maxWidth="md"
        >
            <div className="space-y-2">
                {/* SLA Metrics */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Chỉ số SLA</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            {/* Uptime */}
                            <div className="space-y-1.5">
                                <Label className="text-xs">Uptime (%)</Label>
                                <Controller
                                    name="uptime_guarantee"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            className="h-9"
                                            value={field.value ?? ''}
                                            onChange={(e) => {
                                                const val =
                                                    parseFloat(
                                                        e.target.value
                                                    ) || 0
                                                field.onChange(val)
                                                editingField.current = 'uptime'
                                                setValue(
                                                    'downtime_allowance_minutes',
                                                    calculateDowntime(val)
                                                )
                                                setTimeout(() => {
                                                    editingField.current = null
                                                }, 100)
                                            }}
                                        />
                                    )}
                                />
                                {uptime && (
                                    <p className="text-[10px] text-gray-500">
                                        ~{' '}
                                        {((100 - (uptime || 0)) * 7.2).toFixed(
                                            1
                                        )}{' '}
                                        giờ downtime/tháng
                                    </p>
                                )}
                            </div>

                            {/* Downtime */}
                            <div className="space-y-1.5">
                                <Label className="text-xs">
                                    Downtime (phút)
                                </Label>
                                <Controller
                                    name="downtime_allowance_minutes"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            min="0"
                                            className="h-9"
                                            value={field.value ?? ''}
                                            onChange={(e) => {
                                                const val =
                                                    parseInt(e.target.value) ||
                                                    0
                                                field.onChange(val)
                                                editingField.current =
                                                    'downtime'
                                                setValue(
                                                    'uptime_guarantee',
                                                    calculateUptime(val)
                                                )
                                                setTimeout(() => {
                                                    editingField.current = null
                                                }, 100)
                                            }}
                                        />
                                    )}
                                />
                                {downtime && (
                                    <p className="text-[10px] text-gray-500">
                                        ~ {((downtime || 0) / 60).toFixed(1)}{' '}
                                        giờ/tháng
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                            {/* Response Time */}
                            <div className="space-y-1.5">
                                <Label className="text-xs">
                                    Phản hồi (phút)
                                </Label>
                                <Controller
                                    name="sla_rules.0.response_time_minutes"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            min="1"
                                            className="h-9"
                                            value={field.value ?? 30}
                                            onChange={(e) =>
                                                field.onChange(
                                                    parseInt(e.target.value) ||
                                                        30
                                                )
                                            }
                                        />
                                    )}
                                />
                                {responseTime && responseTime >= 60 && (
                                    <p className="text-[10px] text-gray-500">
                                        = {(responseTime / 60).toFixed(1)} giờ
                                    </p>
                                )}
                            </div>

                            {/* Resolution Time */}
                            <div className="space-y-1.5">
                                <Label className="text-xs">Xử lý (phút)</Label>
                                <Controller
                                    name="sla_rules.0.resolution_time_minutes"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            min="1"
                                            className="h-9"
                                            value={field.value ?? 240}
                                            onChange={(e) =>
                                                field.onChange(
                                                    parseInt(e.target.value) ||
                                                        240
                                                )
                                            }
                                        />
                                    )}
                                />
                                {resolutionTime && resolutionTime >= 60 && (
                                    <p className="text-[10px] text-gray-500">
                                        = {(resolutionTime / 60).toFixed(1)} giờ
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Business Hours Config */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm">
                            Chế độ tính SLA
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* 24/7 Toggle */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium">
                                    Tính 24/7
                                </Label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {businessHoursOnly
                                        ? 'Đang tính theo giờ làm việc'
                                        : 'SLA được tính liên tục'}
                                </p>
                            </div>
                            <Controller
                                name="business_hours_only"
                                control={control}
                                render={({ field }) => (
                                    <Switch
                                        checked={!field.value}
                                        onCheckedChange={(checked) =>
                                            field.onChange(!checked)
                                        }
                                    />
                                )}
                            />
                        </div>

                        {businessHoursOnly && (
                            <>
                                {/* Timezone */}
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-gray-600">
                                        Múi giờ
                                    </Label>
                                    <Controller
                                        name="business_hours_config.timezone"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue placeholder="Chọn múi giờ" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {TIMEZONE_OPTIONS.map(
                                                        (tz) => (
                                                            <SelectItem
                                                                key={tz.value}
                                                                value={tz.value}
                                                            >
                                                                {tz.label}
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>

                                {/* Work Hours */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-gray-600">
                                            Giờ bắt đầu
                                        </Label>
                                        <Controller
                                            name="business_hours_config.work_hours.start"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="time"
                                                    className="h-9"
                                                    value={
                                                        field.value ?? '08:00'
                                                    }
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-gray-600">
                                            Giờ kết thúc
                                        </Label>
                                        <Controller
                                            name="business_hours_config.work_hours.end"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="time"
                                                    className="h-9"
                                                    value={
                                                        field.value ?? '17:30'
                                                    }
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Work Days */}
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-gray-600">
                                        Ngày làm việc
                                    </Label>
                                    <Controller
                                        name="business_hours_config.work_days"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex flex-wrap gap-1">
                                                {WORK_DAYS_OPTIONS.map(
                                                    (day) => {
                                                        const isSelected = (
                                                            field.value || []
                                                        ).includes(day.value)
                                                        return (
                                                            <Button
                                                                key={day.value}
                                                                type="button"
                                                                variant={
                                                                    isSelected
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                size="sm"
                                                                className="h-7 min-w-[36px] px-2 text-xs"
                                                                onClick={() => {
                                                                    const current =
                                                                        field.value ||
                                                                        []
                                                                    const newVal =
                                                                        isSelected
                                                                            ? current.filter(
                                                                                  (
                                                                                      v: number
                                                                                  ) =>
                                                                                      v !==
                                                                                      day.value
                                                                              )
                                                                            : [
                                                                                  ...current,
                                                                                  day.value,
                                                                              ].sort()
                                                                    field.onChange(
                                                                        newVal
                                                                    )
                                                                }}
                                                            >
                                                                {day.label}
                                                            </Button>
                                                        )
                                                    }
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Custom Terms */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm">
                                Điều khoản tùy chỉnh
                            </CardTitle>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs px-2"
                                onClick={() =>
                                    appendTerm({ key: '', value: '' })
                                }
                            >
                                <Plus className="w-3 h-3 mr-1" />
                                Thêm
                            </Button>
                        </div>
                        <CardDescription className="text-xs">
                            Các điều khoản đặc biệt cho SLA
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {customTermFields.length === 0 ? (
                            <div className="text-center py-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500">
                                    Chưa có điều khoản
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {customTermFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="py-3 first:pt-0 last:pb-0"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-gray-500">
                                                #{index + 1}
                                            </span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                                onClick={() =>
                                                    removeTerm(index)
                                                }
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                        <div className="space-y-2">
                                            <Controller
                                                name={`custom_terms.${index}.key`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        placeholder="Tên điều khoản"
                                                        className="h-8 text-xs"
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name={`custom_terms.${index}.value`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        placeholder="Giá trị / Nội dung"
                                                        className="h-8 text-xs"
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Dialog>
    )
}

export default SLAConfigModal
